/**
 * Scout v0 — workspace always-on suppression list panel (P20, §1.3 layer 1).
 *
 * Three typed list editors: clients, competitors, bad-fit. Each has:
 *   - a bulk-paste textarea (newline/comma separated domains or emails),
 *   - a normalized + deduped preview before the round-trip,
 *   - an Add button (POST /scout/suppressions/add),
 *   - the live list with a per-entry remove button,
 *   - a Clear-list action (DELETE /scout/suppressions/:type).
 *
 * COMPETITOR REVIEW-FIRST (locked nuance, §1.3): a recruiter is NOT the
 * end-employer. Competitor entries are flagged review-first — the pipeline
 * reviews before suppressing an identifiable hiring company, never blind-
 * suppress. The competitor list renders a badge + one-line copy explaining
 * this; clients and bad-fit omit it.
 *
 * Entries take effect at compose time (the P15 merge reads the profile). The
 * panel is the workspace settings surface for the always-on tier; the global
 * bouinv tier and per-search learned tier are NOT touched here.
 */

import { useMemo, useState } from 'react';
import {
  useScoutSuppressions,
  useAddSuppressions,
  useRemoveSuppressions,
  useClearSuppressions,
} from '@/lib/scout-queries';
import { getErrorMessage } from '@/lib/queries';
import { parseSuppressionPaste } from '@/lib/suppression-paste';
import { useToast } from '@/components/ui/useToast';
import { ErrorBanner, LoadingSpinner, EmptyState } from '@/components/ui';
import type { SuppressionListType } from '@/types/scout';

/* ─────────────────────────────────────────────────────────────────────── */
/* Per-type config                                                            */
/* ─────────────────────────────────────────────────────────────────────── */

interface TypeConfig {
  type: SuppressionListType;
  label: string;
  description: string;
  /** Competitor-only: show the review-first badge + copy. */
  reviewFirst?: boolean;
}

const TYPES: TypeConfig[] = [
  {
    type: 'clients',
    label: 'Clients',
    description: 'Companies you already work with. Never outreach.',
  },
  {
    type: 'competitors',
    label: 'Competitors',
    description: 'Staffing firms you compete with.',
    reviewFirst: true,
  },
  {
    type: 'badFit',
    label: 'Bad-fit',
    description: 'Companies that never match your placement criteria.',
  },
];

/* ─────────────────────────────────────────────────────────────────────── */
/* Single typed list editor                                                   */
/* ─────────────────────────────────────────────────────────────────────── */

function SuppressionListEditor({ config }: { config: TypeConfig }) {
  const add = useAddSuppressions();
  const remove = useRemoveSuppressions();
  const clear = useClearSuppressions();
  const { addToast } = useToast();

  const [paste, setPaste] = useState('');

  // Client-side parse + dedupe preview (runs on every keystroke).
  const preview = useMemo(() => parseSuppressionPaste(paste), [paste]);
  const canAdd = preview.values.length > 0 && !add.isPending;

  const handleAdd = () => {
    if (preview.values.length === 0) return;
    add.mutate(
      { type: config.type, values: preview.values },
      {
        onSuccess: () => {
          setPaste('');
          addToast('success', `Added ${preview.values.length} entr${preview.values.length === 1 ? 'y' : 'ies'} to ${config.label}.`);
        },
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  const handleRemove = (value: string) => {
    remove.mutate(
      { type: config.type, values: [value] },
      {
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  const handleClear = () => {
    if (!confirm(`Clear all ${config.label.toLowerCase()} entries?`)) return;
    clear.mutate(config.type, {
      onError: (err) => addToast('error', getErrorMessage(err)),
    });
  };

  // The live list comes from the shared query cache (the parent panel loads it
  // and the mutations update the cache in place, so this reads the latest).
  // We access it via the hook so each editor stays self-contained.
  const { data: lists } = useScoutSuppressions();
  const entries = lists?.[config.type] ?? [];

  return (
    <section className="card" aria-labelledby={`supp-${config.type}-heading`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id={`supp-${config.type}-heading`} className="text-lg font-semibold text-text">
            {config.label}
          </h2>
          <p className="text-xs text-muted mt-0.5">{config.description}</p>
        </div>
        {config.reviewFirst ? (
          <span className="badge badge-warning" title="Competitor entries are review-first">
            review-first
          </span>
        ) : (
          <span className="text-xs text-muted">{entries.length} entries</span>
        )}
      </div>

      {config.reviewFirst && (
        <p className="text-xs text-warning mb-3">
          A recruiter is not the end-employer. Competitor entries are reviewed before suppressing an identifiable hiring company.
        </p>
      )}

      {/* Bulk paste */}
      <div className="space-y-2">
        <label htmlFor={`supp-${config.type}-paste`} className="block text-sm font-medium text-text">
          Add domains or emails
        </label>
        <textarea
          id={`supp-${config.type}-paste`}
          className="input min-h-[80px] font-mono text-sm"
          placeholder="acme.com&#10;rivalstaff.com&#10;ceo@beta.io"
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          disabled={add.isPending}
          aria-describedby={`supp-${config.type}-preview`}
        />

        {/* Normalized + deduped preview */}
        {paste.trim() && (
          <div id={`supp-${config.type}-preview`} className="text-xs space-y-1">
            {preview.values.length > 0 && (
              <p className="text-success">
                {preview.values.length} to add: {preview.values.join(', ')}
              </p>
            )}
            {preview.duplicatesDropped > 0 && (
              <p className="text-muted">{preview.duplicatesDropped} duplicate(s) dropped.</p>
            )}
            {preview.invalid.length > 0 && (
              <p className="text-warning">
                {preview.invalid.length} invalid (skipped): {preview.invalid.map((i) => i.raw).join(', ')}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-primary"
            onClick={handleAdd}
            disabled={!canAdd}
          >
            {add.isPending ? 'Adding…' : `Add to ${config.label}`}
          </button>
          {add.isError && (
            <span className="text-xs text-warning">{getErrorMessage(add.error)}</span>
          )}
        </div>
      </div>

      {/* Live list */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted">
            Current list {config.reviewFirst && `· ${entries.length} entries`}
          </p>
          {entries.length > 0 && (
            <button
              type="button"
              className="btn-ghost text-sm text-warning"
              onClick={handleClear}
              disabled={clear.isPending}
            >
              Clear all
            </button>
          )}
        </div>

        {entries.length === 0 ? (
          <p className="text-sm text-muted italic">(none)</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {entries.map((value) => (
              <li
                key={value}
                className="badge badge-warning inline-flex items-center gap-1.5"
                title={config.reviewFirst ? 'Competitor entry (review-first)' : 'Always-on suppression'}
              >
                <span className="font-mono">{value}</span>
                <button
                  type="button"
                  className="text-warning hover:text-text"
                  onClick={() => handleRemove(value)}
                  disabled={remove.isPending}
                  aria-label={`Remove ${value}`}
                  title="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {remove.isError && (
          <p className="text-xs text-warning mt-2">{getErrorMessage(remove.error)}</p>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Panel — loads the shared suppressions query, renders the three editors   */
/* ─────────────────────────────────────────────────────────────────────── */

export function SuppressionListPanel() {
  const { isLoading, error, refetch } = useScoutSuppressions();

  if (isLoading) {
    return (
      <div className="card">
        <LoadingSpinner className="py-6" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorBanner
        message={getErrorMessage(error)}
        retryable={error.retryable}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <EmptyState
          title="Always-on suppressions"
          description="These ride into every composed search as the NOT group. Edited here, never shared across workspaces. Entries take effect at the next compose."
        />
      </div>
      {TYPES.map((config) => (
        <SuppressionListEditor key={config.type} config={config} />
      ))}
    </div>
  );
}